import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, defineProperty as _defineProperty } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useRef, useCallback, useMemo } from 'react';
import { jsx } from '@emotion/react';
import { StickyControlContext, StickyValueContext } from '../contexts/internal/StickyContext.js';
import '../contexts/internal/EditorContext.js';

var STICKY_ENTRY_STATE_KEYS = ['disabled', 'emulatedDisabled', 'height', 'bottom', 'beginY'];
var DIRECTIONS = ['top', 'bottom'];
function StickyProvider(props) {
  var children = props.children;
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    entries = _useState2[0],
    setEntries = _useState2[1];
  var entriesRef = useRef(entries);
  entriesRef.current = entries;
  var shouldSortEntriesRef = useRef(false);
  var entriesOrderRef = useRef({
    top: {
      ids: [],
      parentIndices: []
    },
    bottom: {
      ids: [],
      parentIndices: []
    }
  });
  var updateEntries = useCallback(function (getState) {
    return setEntries(function (prevState) {
      // top/bottom sticky objects are unrelated with each other. Therefore
      // run entire routine for each direction.
      var nextState = getState(prevState);
      if (nextState === prevState) {
        return prevState;
      }
      var entriesMap = nextState;
      var resultSet = _objectSpread2({}, nextState);
      DIRECTIONS.forEach(function (direction) {
        var orders = entriesOrderRef.current[direction];
        if (shouldSortEntriesRef.current) {
          // Sort the sticky entries according to the DOM order.
          // ... This can be done relatively easily IF we had shared className,
          // or attribute available to search using querySelectorAll().
          // However, we can't determine that's the case - this can be provided
          // with HTMLElement - no other requirements are specified - therefore
          // we have to handle every case.
          // Therefore... we must implement graph traversal.
          // - The sorting algorithm doesn't need to be implemented, as
          //   array.sort() can receive comparator function.
          // - While sorthing through array.sort(), comparator function gets
          //   called O(n log n) times. Since we can assume that sticky nodes
          //   exist on the screen AT MOST 10, this is pretty much negligble.
          // - The comparator function should just call
          //   'compareDocumentPosition()' method to compare the position
          //   between two elements.
          // Note that .sort() method is mutable.
          // NOTE: This routine will not work properly if the sticky element is
          // between multiple document, for example, iframes, or window.open().
          // Since we can assume that this will not happen, we can ignore this.
          // However this is pretty trivial to resolve since we just need to
          // attach new StickyProvider onto the new document's elements.
          var dirEntries = Object.keys(entriesMap).map(function (key) {
            return entriesMap[key];
          }).filter(function (item) {
            return item.direction === direction;
          });
          var orderedItems = dirEntries.sort(function (a, b) {
            var aElem = a.ref.current;
            var bElem = b.ref.current;
            if (aElem == null) {
              return 1;
            }
            if (bElem == null) {
              return -1;
            }
            var compareResult = aElem.compareDocumentPosition(bElem);
            if (compareResult & Node.DOCUMENT_POSITION_FOLLOWING) {
              return -1;
            }
            if (compareResult & Node.DOCUMENT_POSITION_PRECEDING) {
              return 1;
            }
            return 0;
          });
          var ids = orderedItems.map(function (v) {
            return v.id;
          });
          // Flip the list if the direction is bottom.
          if (direction === 'bottom') {
            ids = ids.reverse();
          }
          // Determine the Sticky dependency graph parent indices. Since the
          // 'orders.ids' already stores sorted IDs, we can use that to quickly
          // derive parent indices.

          // NOTE: While it should be possible to determine parent IDs and order
          // at the same time, the logic is already mind-boggling at this
          // point. To avoid making this difficult, sorting -> backreferencing
          // can be applied to quickly determine the parents of the Sticky node.
          var parentIndices = [];
          for (var i = 0; i < ids.length; i += 1) {
            var item = entriesMap[ids[i]];
            if (item == null) {
              continue;
            }
            var itemElem = item.ref.current;
            if (itemElem == null) {
              continue;
            }
            // For each node, determine the "parent" node, excluding the
            // siblings.
            // Due to this nested loop, the time complexity would be
            // O(n^2 log m). Still, this is acceptable as n is already too
            // small.
            var targetIndex = -1;
            for (var j = i - 1; j >= 0; j -= 1) {
              var target = entriesMap[ids[j]];
              if (target == null) {
                continue;
              }
              var targetElem = target.ref.current;
              if (targetElem == null) {
                continue;
              }
              var targetParentElem = targetElem.parentElement;
              if (targetParentElem == null) {
                continue;
              }
              // Check if the target:
              // - "has no bottom" therefore it should always stick regardless
              //   of DOM node hierarchy
              // - 's parent element contains itemElem. When this is true,
              //   target element is on the top/bottom of the item element -
              //   which means that the offset should use data from there.
              //
              // Let's skip "disableBottom" for now - the "contains" logic
              // works like this:
              //
              // 1. In the following case, B should be below A since
              //    A will always be visible while looking at B.
              // div
              //  +------+------------+
              //        A             B
              //
              // 2. It doesn't matter if B is below some node too.
              // div
              //  +------+------------+
              //         A           div
              //                      +--------+
              //                               B
              //
              // 3. HOWEVER, if A is below some node, it should not stick
              //    with each other, as A and B will never intersect, since
              //    the Sticky's scope only expand's to its parent element.
              //
              // div
              //  +------+------------+
              //        div           B
              //         +
              //         A
              //
              // For these cases, A.parentElement.contains(B) should check
              // all these (as the nodes are already ordered at this point)
              // and return the correct result. Therefore, we're just
              // only relying on this logic.

              // NOTE: This WILL break if the code uses
              // flex-direction: row-reverse or something like that. We hope
              // that will never happen...
              if (target.disableBottom || targetParentElem.contains(itemElem)) {
                // Found an adequate item, set the index and break
                targetIndex = j;
                break;
              }
            }
            parentIndices[i] = targetIndex;
          }
          orders.ids = ids;
          orders.parentIndices = parentIndices;
          entriesOrderRef.current[direction] = orders;
        }
        // Determine the new accumulatedOffset...
        var accumulatedOffsets = [];
        var accumulatedOffsetRanges = [];
        orders.ids.forEach(function (key, order) {
          var _accumulatedOffsets$p, _accumulatedOffsetRan;
          var parentIndex = orders.parentIndices[order];
          var parentOffset = parentIndex === -1 ? 0 : (_accumulatedOffsets$p = accumulatedOffsets[parentIndex]) !== null && _accumulatedOffsets$p !== void 0 ? _accumulatedOffsets$p : 0;
          var parentOffsetRange = parentIndex === -1 ? [0, 0] : (_accumulatedOffsetRan = accumulatedOffsetRanges[parentIndex]) !== null && _accumulatedOffsetRan !== void 0 ? _accumulatedOffsetRan : [0, 0];
          var item = entriesMap[key];
          if (item == null || parentIndex == null) {
            return;
          }
          if (item.accumulatedOffset !== parentOffset || item.order !== order) {
            resultSet[key] = _objectSpread2(_objectSpread2({}, item), {}, {
              accumulatedOffset: parentOffset,
              accumulatedOffsetRange: parentOffsetRange,
              order: order
            });
          }
          if (!item.disabled && !item.bottom) {
            var _item$height, _item$height2, _item$offsetRange$, _item$offsetRange, _item$height3, _item$offsetRange$2, _item$offsetRange2;
            accumulatedOffsets[order] = parentOffset + item.offset + ((_item$height = item.height) !== null && _item$height !== void 0 ? _item$height : 0);
            accumulatedOffsetRanges[order] = [parentOffsetRange[0] + ((_item$height2 = item.height) !== null && _item$height2 !== void 0 ? _item$height2 : 0) + ((_item$offsetRange$ = (_item$offsetRange = item.offsetRange) === null || _item$offsetRange === void 0 ? void 0 : _item$offsetRange[0]) !== null && _item$offsetRange$ !== void 0 ? _item$offsetRange$ : item.offset), parentOffsetRange[1] + ((_item$height3 = item.height) !== null && _item$height3 !== void 0 ? _item$height3 : 0) + ((_item$offsetRange$2 = (_item$offsetRange2 = item.offsetRange) === null || _item$offsetRange2 === void 0 ? void 0 : _item$offsetRange2[1]) !== null && _item$offsetRange$2 !== void 0 ? _item$offsetRange$2 : item.offset)];
          } else {
            accumulatedOffsets[order] = parentOffset;
            accumulatedOffsetRanges[order] = parentOffsetRange;
          }
        });
      });
      shouldSortEntriesRef.current = false;
      return resultSet;
    });
  }, []);
  var handleRegister = useCallback(function () {
    shouldSortEntriesRef.current = true;
    // Assign a random ID
    // NOTE: This is NOT deterministic and therefore there can be
    // inconsistencies between the server and the client while doing SSR.
    // However, this ID is never exposed outside React state and therefore
    // there won't be any discrepancies in the resulting HTML itself.
    return String(Math.random() * 0x7FFFFFFF | 0);
  }, [setEntries]);
  var handleUpdate = useCallback(function (id, containerRef, options, state) {
    updateEntries(function (prevState) {
      var _options$shouldInflue, _options$shouldInflue2;
      // Check if the related state has changed so that we have to recalculate
      // the accumulatedOffset of sibling states.
      var prevItem = prevState[id];
      if (prevItem == null) {
        // New item has appended
        shouldSortEntriesRef.current = true;
        prevItem = {
          id: id,
          type: undefined,
          shouldInfluenceScroll: true,
          direction: null,
          disabled: true,
          emulatedDisabled: true,
          disableBottom: false,
          bottom: false,
          height: 0,
          offset: 0,
          accumulatedOffset: 0,
          accumulatedOffsetRange: [0, 0],
          beginY: 0,
          ref: containerRef,
          order: 0
        };
      }
      var hasChanged = STICKY_ENTRY_STATE_KEYS.some(function (key) {
        return prevItem[key] !== state[key];
      }) || prevItem.direction !== options.direction || prevItem.offset !== options.offset || prevItem.disableBottom !== options.disableBottom || prevItem.type !== options.type || prevItem.shouldInfluenceScroll !== ((_options$shouldInflue = options.shouldInfluenceScroll) !== null && _options$shouldInflue !== void 0 ? _options$shouldInflue : true);
      // offsetRange is omitted from diff calculation, as it can trigger
      // unnecessary updates
      if (!hasChanged) {
        // Nothing has changed; do nothing
        return prevState;
      }
      if (prevItem.direction !== options.direction) {
        // The sticky's direction has changed - re-sort the set
        shouldSortEntriesRef.current = true;
      }
      // Overwrite the related value
      return _objectSpread2(_objectSpread2({}, prevState), {}, _defineProperty({}, id, _objectSpread2(_objectSpread2({}, prevItem), {}, {
        direction: options.direction,
        disabled: state.disabled,
        emulatedDisabled: state.emulatedDisabled,
        height: state.height,
        bottom: state.bottom,
        beginY: state.beginY,
        offset: options.offset,
        offsetRange: options.offsetRange,
        disableBottom: options.disableBottom,
        type: options.type,
        shouldInfluenceScroll: (_options$shouldInflue2 = options.shouldInfluenceScroll) !== null && _options$shouldInflue2 !== void 0 ? _options$shouldInflue2 : true
      })));
    });
  }, [updateEntries]);
  var handleUnregister = useCallback(function (id) {
    shouldSortEntriesRef.current = true;
    updateEntries(function (prevState) {
      var newState = _objectSpread2({}, prevState);
      delete newState[id];
      return newState;
    });
  }, [updateEntries]);
  var handleGetState = useCallback(function () {
    return {
      entries: entriesRef.current
    };
  }, []);
  var controlValue = useMemo(function () {
    return {
      register: handleRegister,
      update: handleUpdate,
      unregister: handleUnregister,
      getState: handleGetState
    };
  }, [handleRegister, handleUpdate, handleUnregister, handleGetState]);
  return jsx(StickyControlContext.Provider, {
    value: controlValue
  }, jsx(StickyValueContext.Provider, {
    value: {
      entries: entries
    }
  }, children));
}

export { StickyProvider };
//# sourceMappingURL=StickyProvider.js.map
